import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Palette, Upload, Trash2, Loader2, History, Mic, Globe2,
  Plus, Save, X, ShieldCheck, AlertCircle, Image as ImageIcon, Type,
  BookOpen, Sparkles, FileText, Download, Sliders,
} from "lucide-react";
import StyleProfilePanel from "../components/StyleProfilePanel";
import DesignEthosPanel from "../components/DesignEthosPanel";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

async function apiCall(path, options = {}) {
  return fetch(`${API}${path}`, { credentials: "include", ...options });
}

const COLOUR_SLOTS = [
  { k: "primary",      l: "Primary" },
  { k: "secondary",    l: "Accent" },
  { k: "accent",       l: "Highlight" },
  { k: "bg_dark",      l: "Dark background" },
  { k: "bg_light",     l: "Light background" },
  { k: "text_on_dark", l: "Text on dark" },
  { k: "text_on_light",l: "Text on light" },
  { k: "muted",        l: "Muted" },
];

const LOGO_SLOTS = [
  { k: "primary",     l: "Primary (blue)",      help: "Main logo — used by the composer by default" },
  { k: "white",       l: "White",               help: "For dark overlays / photos with busy shadows" },
  { k: "dark",        l: "Dark",                help: "For light backgrounds / bright photos" },
  { k: "icon",        l: "Icon only",           help: "Square mark — no wordmark. Used for thumbnails" },
  { k: "light",       l: "Light (legacy)",      help: "Older light variant — keep for back-compat" },
  { k: "monochrome",  l: "Monochrome",          help: "Single-colour version for stamps & favicons" },
  { k: "square",      l: "Square crop",         help: "For avatars and social thumbnails" },
  { k: "horizontal",  l: "Horizontal crop",     help: "For email footers and wide banners" },
];

export default function BrandManager({ user }) {
  const [canEdit, setCanEdit] = useState(user?.role === "admin");
  const [designerEmail, setDesignerEmail] = useState(null);
  const [tab, setTab] = useState("identity");
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    apiCall("/brand-manager/designer-config").then(r => r.ok ? r.json() : null).then(cfg => {
      if (!cfg) return;
      setDesignerEmail(cfg.designer_email);
      const email = (user?.email || "").toLowerCase();
      if (user?.role === "admin" || (cfg.designer_email && email === cfg.designer_email.toLowerCase())) {
        setCanEdit(true);
      }
    });
  }, [user]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="brand-manager-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Palette className="h-7 w-7 text-indigo-600" /> Brand Manager
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Teach the system how RESILIGENT should look and sound. Changes here flow into every
            auto-rendered post, Smart Wizard draft, brand-drift check, and the generated Brand Manual.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} data-testid="history-btn">
          <History className="h-4 w-4 mr-1.5" /> Edit history
        </Button>
      </header>

      {!canEdit && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" />
          Read-only — brand editing is restricted to admins{designerEmail ? ` and the designer (${designerEmail})` : ""}.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="identity" data-testid="tab-identity">
            <Palette className="h-4 w-4 mr-1.5" /> Identity
          </TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">
            <Mic className="h-4 w-4 mr-1.5" /> Voice &amp; Markets
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">
            <BookOpen className="h-4 w-4 mr-1.5" /> Brand Manual
          </TabsTrigger>
          <TabsTrigger value="style" data-testid="tab-style-profile">
            <Sliders className="h-4 w-4 mr-1.5" /> Style Profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-4">
          <IdentityTab userIsAdmin={canEdit} />
        </TabsContent>
        <TabsContent value="voice" className="mt-4">
          <VoiceMarketsTab userIsAdmin={canEdit} />
        </TabsContent>
        <TabsContent value="manual" className="mt-4">
          <BrandManualTab userIsAdmin={canEdit} />
        </TabsContent>
        <TabsContent value="style" className="mt-4">
          <StyleProfilePanel userIsAdmin={canEdit} />
          <div className="my-6 border-t border-slate-200" />
          <DesignEthosPanel userIsAdmin={canEdit} />
        </TabsContent>
      </Tabs>

      <HistoryDialog open={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
}


// ============================================================================
// Identity tab
// ============================================================================
function IdentityTab({ userIsAdmin }) {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await apiCall("/brand-manager/identity");
    if (r.ok) setKit(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!kit) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const setField = (k, v) => setKit(p => ({ ...p, [k]: v }));
  const setColour = (k, v) => setKit(p => ({ ...p, colors: { ...(p?.colors || {}), [k]: v } }));
  const setRulesText = (text) => {
    const lines = text.split("\n").map(s => s.trim()).filter(Boolean);
    setKit(p => ({ ...p, capitalisation_rules: lines }));
  };

  const save = async () => {
    if (!userIsAdmin) { toast.error("Admin required"); return; }
    setSaving(true);
    const r = await apiCall("/brand-manager/identity", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        brand_name: kit.brand_name,
        brand_tagline: kit.brand_tagline,
        logo_url: kit.logo_url,
        colors: kit.colors,
        capitalisation_rules: kit.capitalisation_rules || [],
      }),
    });
    setSaving(false);
    if (r.ok) {
      toast.success("Identity saved");
      setKit(await r.json());
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Brand basics */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="identity-basics">
        <h2 className="text-base font-semibold">Brand basics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Brand name</Label>
            <Input
              disabled={!userIsAdmin}
              value={kit.brand_name || ""}
              onChange={e => setField("brand_name", e.target.value)}
              data-testid="brand-name-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">Displayed in the auto-rendered card footer.</p>
          </div>
          <div>
            <Label className="text-xs">Tagline</Label>
            <Input
              disabled={!userIsAdmin}
              value={kit.brand_tagline || ""}
              onChange={e => setField("brand_tagline", e.target.value)}
              data-testid="brand-tagline-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Capitalisation rules</Label>
          <Textarea
            disabled={!userIsAdmin}
            value={(kit.capitalisation_rules || []).join("\n")}
            onChange={e => setRulesText(e.target.value)}
            rows={4}
            placeholder="One rule per line (e.g. RESILIGENT)"
            data-testid="cap-rules-textarea"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Exact-match, case-sensitive. Brand-drift check will flag drafts that misspell these.
          </p>
        </div>
      </section>

      {/* Colour palette */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="identity-colours">
        <h2 className="text-base font-semibold">Colour palette</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {COLOUR_SLOTS.map(({ k, l }) => (
            <div key={k} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  disabled={!userIsAdmin}
                  value={kit.colors?.[k] || "#000000"}
                  onChange={e => setColour(k, e.target.value)}
                  className="h-9 w-9 rounded border border-slate-300 cursor-pointer"
                  data-testid={`color-${k}`}
                />
                <span className="font-mono text-xs text-slate-600">{kit.colors?.[k] || "—"}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Logo variants */}
      <LogoUploader userIsAdmin={userIsAdmin} kit={kit} onChange={setKit} />

      {/* Fonts */}
      <FontUploader userIsAdmin={userIsAdmin} kit={kit} onChange={setKit} />

      {userIsAdmin && (
        <div className="sticky bottom-0 z-10 py-3 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent">
          <Button onClick={save} disabled={saving} size="lg" data-testid="save-identity-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save identity
          </Button>
        </div>
      )}
    </div>
  );
}

function LogoUploader({ userIsAdmin, kit, onChange }) {
  const [uploading, setUploading] = useState(null);
  const upload = async (variant, file) => {
    if (!userIsAdmin) { toast.error("Admin required"); return; }
    setUploading(variant);
    const fd = new FormData();
    fd.append("variant", variant);
    fd.append("file", file);
    const r = await apiCall("/brand-manager/identity/logo", { method: "POST", body: fd });
    setUploading(null);
    if (r.ok) {
      const { logo_variants } = await r.json();
      onChange(p => ({ ...p, logo_variants }));
      toast.success(`${variant} logo uploaded`);
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Upload failed");
    }
  };
  const remove = async (variant) => {
    if (!userIsAdmin) return;
    const r = await apiCall(`/brand-manager/identity/logo/${variant}`, { method: "DELETE" });
    if (r.ok) {
      const { logo_variants } = await r.json();
      onChange(p => ({ ...p, logo_variants }));
      toast.success("Removed");
    }
  };

  const variants = kit.logo_variants || {};

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="identity-logos">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold">Logo variants</h2>
          <p className="text-xs text-slate-500">PNG or JPG. Max 5 MB. Missing variants fall back to Primary at render time.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {LOGO_SLOTS.map(({ k, l, help }) => (
          <div key={k} className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs font-medium text-slate-700">{l}</div>
                <div className="text-[10px] text-slate-500 leading-tight">{help}</div>
              </div>
              {variants[k] && userIsAdmin && (
                <button onClick={() => remove(k)} className="text-slate-400 hover:text-red-600" data-testid={`logo-remove-${k}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="h-20 rounded bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              {variants[k] ? (
                <img src={variants[k]} alt={l} className="max-h-full max-w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-slate-300" />
              )}
            </div>
            {userIsAdmin && (
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && upload(k, e.target.files[0])}
                  data-testid={`logo-input-${k}`}
                />
                <div className="flex items-center justify-center gap-1 text-xs border border-dashed border-slate-300 rounded py-1.5 cursor-pointer hover:bg-slate-100 hover:border-slate-400">
                  {uploading === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  <span>{variants[k] ? "Replace" : "Upload"}</span>
                </div>
              </label>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function FontUploader({ userIsAdmin, kit, onChange }) {
  const [uploading, setUploading] = useState(null);
  const fonts = kit.fonts || {};
  const upload = async (slot, file) => {
    if (!userIsAdmin) return;
    setUploading(slot);
    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file);
    const r = await apiCall("/brand-manager/identity/font", { method: "POST", body: fd });
    setUploading(null);
    if (r.ok) {
      const { fonts: updated } = await r.json();
      onChange(p => ({ ...p, fonts: updated }));
      toast.success(`${slot} font uploaded`);
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Upload failed");
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="identity-fonts">
      <div>
        <h2 className="text-base font-semibold">Fonts</h2>
        <p className="text-xs text-slate-500">
          .ttf or .otf, max 5 MB per file. <span className="text-amber-700">Ensure you have a licence for server-side rendering before uploading commercial fonts.</span>
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["heading", "body", "accent"].map(slot => (
          <div key={slot} className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Type className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-xs font-medium capitalize">{slot}</span>
            </div>
            <div className="text-[11px] font-mono text-slate-500 truncate">
              {fonts[slot] ? fonts[slot].split("/").pop() : "— default —"}
            </div>
            {userIsAdmin && (
              <label className="block">
                <input
                  type="file"
                  accept=".ttf,.otf"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && upload(slot, e.target.files[0])}
                  data-testid={`font-input-${slot}`}
                />
                <div className="flex items-center justify-center gap-1 text-xs border border-dashed border-slate-300 rounded py-1.5 cursor-pointer hover:bg-slate-100">
                  {uploading === slot ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  <span>{fonts[slot] ? "Replace" : "Upload"}</span>
                </div>
              </label>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


// ============================================================================
// Voice & Markets tab
// ============================================================================
function VoiceMarketsTab({ userIsAdmin }) {
  return (
    <div className="space-y-6">
      <VoicesPanel userIsAdmin={userIsAdmin} />
      <MarketsPanel userIsAdmin={userIsAdmin} />
      <StandardsPanel userIsAdmin={userIsAdmin} />
    </div>
  );
}

function listEditor(value, setValue, placeholder, disabled, testId) {
  const text = (value || []).join("\n");
  return (
    <Textarea
      disabled={disabled}
      value={text}
      onChange={e => setValue(e.target.value.split("\n").map(s => s.trim()).filter(Boolean))}
      rows={3}
      placeholder={placeholder}
      data-testid={testId}
      className="text-xs"
    />
  );
}

function VoicesPanel({ userIsAdmin }) {
  const [voices, setVoices] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    const r = await apiCall("/brand-manager/voices?include_inactive=true");
    if (r.ok) setVoices(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="voices-panel">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-1.5"><Mic className="h-4 w-4" /> Voice systems</h2>
          <p className="text-xs text-slate-500">Each voice is a complete system: reasoning chain + anti-drift rules + product mapping + tone + audience.</p>
        </div>
        {userIsAdmin && (
          <Button size="sm" variant="outline" onClick={() => setCreating(true)} data-testid="add-voice-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add voice
          </Button>
        )}
      </div>
      <div className="space-y-4">
        {voices.map(v => <VoiceCard key={v.key} voice={v} userIsAdmin={userIsAdmin} onChange={load} />)}
        {voices.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No voice systems yet.</div>}
      </div>
      {creating && <VoiceCreateDialog onClose={() => setCreating(false)} onSaved={load} />}
    </section>
  );
}

function VoiceCard({ voice, userIsAdmin, onChange }) {
  const [draft, setDraft] = useState(voice);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDraft(voice); setDirty(false); }, [voice]);
  const set = (k, v) => { setDraft(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    const r = await apiCall(`/brand-manager/voices/${voice.key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    setSaving(false);
    if (r.ok) { toast.success("Voice saved"); setDirty(false); onChange(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Save failed"); }
  };
  const toggleActive = async () => {
    if (draft.active) {
      const r = await apiCall(`/brand-manager/voices/${voice.key}`, { method: "DELETE" });
      if (r.ok) { toast.success("Deactivated"); onChange(); }
    } else {
      setDraft(p => ({ ...p, active: true })); setDirty(true);
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${draft.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-80"}`} data-testid={`voice-card-${voice.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <Input
            disabled={!userIsAdmin}
            value={draft.name}
            onChange={e => set("name", e.target.value)}
            className="text-base font-semibold border-transparent hover:border-slate-300 focus:border-slate-400 px-2 -ml-2"
            data-testid={`voice-name-${voice.key}`}
          />
          <div className="text-[11px] font-mono text-slate-400 mt-0.5">key: {voice.key}</div>
        </div>
        {userIsAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant={draft.active ? "default" : "secondary"}>{draft.active ? "Active" : "Inactive"}</Badge>
            <Button size="sm" variant="ghost" onClick={toggleActive} data-testid={`voice-toggle-${voice.key}`}>
              {draft.active ? "Deactivate" : "Reactivate"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs">Description</Label><Textarea disabled={!userIsAdmin} value={draft.description || ""} onChange={e => set("description", e.target.value)} rows={2} className="text-xs" /></div>
        <div><Label className="text-xs">Tone</Label><Textarea disabled={!userIsAdmin} value={draft.tone || ""} onChange={e => set("tone", e.target.value)} rows={2} className="text-xs" /></div>
        <div><Label className="text-xs">Audience</Label><Textarea disabled={!userIsAdmin} value={draft.audience || ""} onChange={e => set("audience", e.target.value)} rows={2} className="text-xs" /></div>
        <div><Label className="text-xs">Display order</Label><Input disabled={!userIsAdmin} type="number" value={draft.display_order || 100} onChange={e => set("display_order", parseInt(e.target.value) || 100)} /></div>
        <div><Label className="text-xs">Reasoning chain</Label>{listEditor(draft.reasoning_chain, v => set("reasoning_chain", v), "One step per line", !userIsAdmin, `voice-reasoning-${voice.key}`)}</div>
        <div><Label className="text-xs">Anti-drift rules</Label>{listEditor(draft.anti_drift_rules, v => set("anti_drift_rules", v), "One rule per line", !userIsAdmin, `voice-antidrift-${voice.key}`)}</div>
        <div className="md:col-span-2"><Label className="text-xs">Product mapping</Label>{listEditor(draft.product_mapping, v => set("product_mapping", v), "One rule per line", !userIsAdmin, `voice-product-${voice.key}`)}</div>
      </div>
      {userIsAdmin && dirty && (
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={save} disabled={saving} data-testid={`voice-save-${voice.key}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
          </Button>
        </div>
      )}
    </div>
  );
}

function VoiceCreateDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ key: "", name: "", description: "", tone: "", audience: "", reasoning_chain: [], anti_drift_rules: [], product_mapping: [], display_order: 100, active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.key || !form.name) { toast.error("Key and name required"); return; }
    setSaving(true);
    const r = await apiCall("/brand-manager/voices", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) { toast.success("Voice created"); onSaved(); onClose(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Create failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="voice-create-dialog">
        <DialogHeader><DialogTitle>Add voice system</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Key (slug)</Label><Input value={form.key} onChange={e => set("key", e.target.value)} placeholder="e.g. market_authority" data-testid="voice-new-key" /></div>
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="voice-new-name" /></div>
          <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} /></div>
          <div><Label className="text-xs">Tone</Label><Input value={form.tone} onChange={e => set("tone", e.target.value)} /></div>
          <div><Label className="text-xs">Audience</Label><Input value={form.audience} onChange={e => set("audience", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="voice-create-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MarketsPanel({ userIsAdmin }) {
  const [markets, setMarkets] = useState([]);
  const load = useCallback(async () => {
    const r = await apiCall("/brand-manager/markets?include_inactive=true");
    if (r.ok) setMarkets(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="markets-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-1.5"><Globe2 className="h-4 w-4" /> Markets &amp; demographics</h2>
        <p className="text-xs text-slate-500">Select a market when drafting and the demographics below get injected into the prompt — pain points, cultural dos and don'ts, English style, seniority.</p>
      </div>
      <div className="space-y-3">
        {markets.map(m => <MarketCard key={m.key} market={m} userIsAdmin={userIsAdmin} onChange={load} />)}
        {markets.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No markets seeded yet — restart backend to seed defaults.</div>}
      </div>
    </section>
  );
}

function MarketCard({ market, userIsAdmin, onChange }) {
  const [draft, setDraft] = useState(market);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { setDraft(market); setDirty(false); }, [market]);
  const set = (k, v) => { setDraft(p => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    const r = await apiCall(`/brand-manager/markets/${market.key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    setSaving(false);
    if (r.ok) { toast.success("Market saved"); setDirty(false); onChange(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Save failed"); }
  };

  const completeness = [
    draft.primary_audience, draft.seniority,
    (draft.pain_points || []).length, (draft.cultural_dos || []).length, (draft.cultural_donts || []).length,
  ].filter(x => x && x !== 0).length;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`market-card-${market.key}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="font-semibold text-sm">{draft.name}</div>
          <div className="text-[11px] font-mono text-slate-400">{market.key}</div>
          <Badge variant={completeness >= 4 ? "default" : "secondary"}>{completeness}/5 demographics filled</Badge>
        </div>
        <span className="text-slate-400 text-xs">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 space-y-3 bg-slate-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Hook framing</Label><Textarea disabled={!userIsAdmin} value={draft.hook_framing || ""} onChange={e => set("hook_framing", e.target.value)} rows={2} className="text-xs" /></div>
            <div><Label className="text-xs">Proof strip language</Label><Textarea disabled={!userIsAdmin} value={draft.proof_strip_language || ""} onChange={e => set("proof_strip_language", e.target.value)} rows={2} className="text-xs" /></div>
            <div><Label className="text-xs">Amplification language</Label><Textarea disabled={!userIsAdmin} value={draft.amplification_language || ""} onChange={e => set("amplification_language", e.target.value)} rows={2} className="text-xs" /></div>
            <div><Label className="text-xs">Cross-post notes</Label><Textarea disabled={!userIsAdmin} value={draft.cross_post_notes || ""} onChange={e => set("cross_post_notes", e.target.value)} rows={2} className="text-xs" /></div>
          </div>
          <div className="border-t border-slate-200 pt-3 mt-2">
            <div className="text-xs font-semibold text-slate-700 mb-2">Demographics</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Primary audience</Label><Input disabled={!userIsAdmin} value={draft.primary_audience || ""} onChange={e => set("primary_audience", e.target.value)} placeholder="e.g. CEOs of Nigerian banks, CHROs of NSE-listed firms" data-testid={`market-audience-${market.key}`} /></div>
              <div><Label className="text-xs">English style</Label><Input disabled={!userIsAdmin} value={draft.english_style || ""} onChange={e => set("english_style", e.target.value)} placeholder="e.g. Nigerian English" data-testid={`market-english-${market.key}`} /></div>
              <div><Label className="text-xs">Age range</Label><Input disabled={!userIsAdmin} value={draft.age_range || ""} onChange={e => set("age_range", e.target.value)} placeholder="e.g. 40-60" /></div>
              <div><Label className="text-xs">Seniority</Label><Input disabled={!userIsAdmin} value={draft.seniority || ""} onChange={e => set("seniority", e.target.value)} placeholder="e.g. C-suite + boards" data-testid={`market-seniority-${market.key}`} /></div>
              <div><Label className="text-xs">Pain points</Label>{listEditor(draft.pain_points, v => set("pain_points", v), "One per line", !userIsAdmin, `market-pain-${market.key}`)}</div>
              <div><Label className="text-xs">Cultural dos</Label>{listEditor(draft.cultural_dos, v => set("cultural_dos", v), "One per line", !userIsAdmin, `market-dos-${market.key}`)}</div>
              <div className="md:col-span-2"><Label className="text-xs">Cultural don'ts</Label>{listEditor(draft.cultural_donts, v => set("cultural_donts", v), "One per line", !userIsAdmin, `market-donts-${market.key}`)}</div>
            </div>
          </div>
          {userIsAdmin && dirty && (
            <div className="flex justify-end">
              <Button size="sm" onClick={save} disabled={saving} data-testid={`market-save-${market.key}`}>
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save market
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function StandardsPanel({ userIsAdmin }) {
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiCall("/brand-manager/standards");
    if (r.ok) setStandards(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const retire = async (id) => {
    if (!window.confirm("Retire this standard? Rex will stop applying it.")) return;
    const r = await apiCall(`/brand-manager/standards/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Retired"); load(); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="standards-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Founder standards</h2>
        <p className="text-xs text-slate-500">Learned from your corrections in Ask Rex. Retire anything stale — or go to /ask-rex and teach Rex a new one.</p>
      </div>
      {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> :
        standards.length === 0 ? <div className="text-sm text-slate-500 py-4 text-center">No founder standards yet.</div> :
        <div className="space-y-2">
          {standards.map(s => <StandardRow key={s.standard_id} s={s} userIsAdmin={userIsAdmin} onRetire={retire} onSaved={load} />)}
        </div>
      }
    </section>
  );
}

function StandardRow({ s, userIsAdmin, onRetire, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s);
  const save = async () => {
    const r = await apiCall(`/brand-manager/standards/${s.standard_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ principle: draft.principle, context: draft.context, domain: draft.domain }),
    });
    if (r.ok) { toast.success("Updated"); setEditing(false); onSaved(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Update failed"); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`standard-${s.standard_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1">
            <Badge variant="secondary">{s.domain || "—"}</Badge>
            {s.context && <span>· {s.context}</span>}
          </div>
          {editing ? (
            <Textarea value={draft.principle || ""} onChange={e => setDraft(p => ({ ...p, principle: e.target.value }))} rows={2} className="text-sm" />
          ) : (
            <div className="text-sm text-slate-800">{s.principle || "—"}</div>
          )}
        </div>
        {userIsAdmin && (
          <div className="flex items-center gap-1 shrink-0">
            {editing ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(s); }}><X className="h-3.5 w-3.5" /></Button>
                <Button size="sm" onClick={save}><Save className="h-3.5 w-3.5" /></Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`standard-edit-${s.standard_id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => onRetire(s.standard_id)} className="text-red-600 hover:text-red-700" data-testid={`standard-retire-${s.standard_id}`}>Retire</Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// History dialog
// ============================================================================
function HistoryDialog({ open, onClose }) {
  const [filter, setFilter] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    const q = filter ? `?entity_type=${filter}` : "";
    const r = await apiCall(`/brand-manager/history${q}`);
    if (r.ok) setHistory(await r.json());
    setLoading(false);
  }, [open, filter]);
  useEffect(() => { load(); }, [load]);

  const fmt = (v) => {
    if (v === null || v === undefined) return <span className="text-slate-400 italic">none</span>;
    if (Array.isArray(v)) return <span className="font-mono text-[11px]">[{v.length} items]</span>;
    if (typeof v === "object") return <span className="font-mono text-[11px]">{JSON.stringify(v).slice(0, 60)}…</span>;
    const s = String(v);
    return <span className="font-mono text-[11px]">{s.length > 80 ? s.slice(0, 77) + "…" : s}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="history-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Edit history</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          <span>Filter:</span>
          {["", "brand_kit", "voice_system", "market", "founder_standard"].map(k => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-2 py-1 rounded border ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`history-filter-${k || "all"}`}>
              {k || "All"}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto -mx-1 px-1 flex-1">
          {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> :
            history.length === 0 ? <div className="py-6 text-center text-sm text-slate-500">No history entries.</div> :
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="text-left py-1.5 font-medium">When</th>
                  <th className="text-left py-1.5 font-medium">Who</th>
                  <th className="text-left py-1.5 font-medium">Entity</th>
                  <th className="text-left py-1.5 font-medium">Field</th>
                  <th className="text-left py-1.5 font-medium">Before</th>
                  <th className="text-left py-1.5 font-medium">After</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.history_id} className="border-b border-slate-100">
                    <td className="py-1.5 whitespace-nowrap text-slate-500">{new Date(h.timestamp).toLocaleString()}</td>
                    <td className="py-1.5 whitespace-nowrap">{h.editor_email}</td>
                    <td className="py-1.5 whitespace-nowrap"><Badge variant="secondary">{h.entity_type}</Badge> <span className="font-mono text-slate-400">{h.entity_id}</span></td>
                    <td className="py-1.5 whitespace-nowrap font-mono">{h.field}</td>
                    <td className="py-1.5">{fmt(h.old_value)}</td>
                    <td className="py-1.5">{fmt(h.new_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Brand Manual tab
// ============================================================================
const MANUAL_SECTIONS = [
  { k: "overview",                l: "Overview" },
  { k: "visual_identity",         l: "Visual Identity" },
  { k: "logo_usage",              l: "Logo Usage & Variations" },
  { k: "typography",              l: "Typography" },
  { k: "voice_architecture",      l: "Voice Architecture" },
  { k: "markets_and_audiences",   l: "Markets & Audiences" },
  { k: "language_standards",      l: "Language Standards" },
  { k: "writing_standards",       l: "Writing Standards" },
  { k: "do_not_use",              l: "Do Not Use" },
];

const DOC_CATEGORIES = [
  { v: "style_guide",    l: "Style guide" },
  { v: "logo_guide",     l: "Logo guide" },
  { v: "visual_sample",  l: "Visual sample" },
  { v: "reference",      l: "Reference" },
  { v: "other",          l: "Other" },
];

function BrandManualTab({ userIsAdmin }) {
  const [docs, setDocs] = useState([]);
  const [manual, setManual] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const loadDocs = useCallback(async () => {
    const r = await apiCall("/brand-manager/documents");
    if (r.ok) setDocs(await r.json());
  }, []);
  const loadManual = useCallback(async () => {
    const r = await apiCall("/brand-manager/manual");
    if (r.ok) {
      const m = await r.json();
      setManual(m);
      setDraft(m.sections || {});
    }
  }, []);
  useEffect(() => { loadDocs(); loadManual(); }, [loadDocs, loadManual]);

  const upload = async (file, category) => {
    if (!userIsAdmin) { toast.error("Designer or admin role required"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    const r = await apiCall("/brand-manager/documents", { method: "POST", body: fd });
    setUploading(false);
    if (r.ok) { toast.success("Document uploaded"); loadDocs(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Upload failed"); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    const r = await apiCall(`/brand-manager/documents/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Removed"); loadDocs(); }
  };

  const generate = async () => {
    if (!userIsAdmin) { toast.error("Designer or admin role required"); return; }
    if (!window.confirm("Regenerate the brand manual from the current state? Any unsaved edits in the sections will be overwritten.")) return;
    setGenerating(true);
    const r = await apiCall("/brand-manager/manual/generate", { method: "POST" });
    setGenerating(false);
    if (r.ok) {
      const m = await r.json();
      setManual(m);
      setDraft(m.sections || {});
      toast.success(m.used_fallback ? "Manual assembled (fallback mode — no LLM)" : "Brand manual regenerated");
    } else {
      const e = await r.json().catch(() => ({})); toast.error(e.detail || "Generation failed");
    }
  };

  const saveManual = async () => {
    if (!userIsAdmin) return;
    setSaving(true);
    const r = await apiCall("/brand-manager/manual", {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sections: draft }),
    });
    setSaving(false);
    if (r.ok) { toast.success("Manual saved"); setManual(await r.json()); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Save failed"); }
  };

  const download = () => {
    if (!manual) return;
    const md = MANUAL_SECTIONS.map(({ k, l }) => {
      const body = draft[k] || "";
      return `# ${l}\n\n${body}\n`;
    }).join("\n---\n\n");
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brand-manual-v${manual.version || 1}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dirty = manual && JSON.stringify(draft) !== JSON.stringify(manual.sections || {});

  return (
    <div className="space-y-6">
      {/* Documents uploader */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="manual-docs">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4" /> Reference documents</h2>
            <p className="text-xs text-slate-500">Upload style guides, logo guides, past decks — the AI uses the extracted text when regenerating the manual.</p>
          </div>
        </div>

        {userIsAdmin && (
          <div className="flex flex-wrap gap-2">
            {DOC_CATEGORIES.map(({ v, l }) => (
              <label key={v} className="inline-flex">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.pptx,.png,.jpg,.jpeg,.webp,.txt,.md"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && upload(e.target.files[0], v)}
                  data-testid={`doc-input-${v}`}
                />
                <span className="inline-flex items-center gap-1.5 text-xs border border-dashed border-slate-300 rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-100">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload {l}
                </span>
              </label>
            ))}
          </div>
        )}

        {docs.length === 0 ? (
          <div className="text-xs text-slate-500 py-3">No reference documents uploaded yet.</div>
        ) : (
          <div className="space-y-1.5">
            {docs.filter(d => d.category !== "social_media_design").map(d => (
              <div key={d.doc_id} className="flex items-center justify-between gap-3 text-xs bg-slate-50 rounded-md px-3 py-2 border border-slate-200" data-testid={`doc-${d.doc_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <a href={d.url} target="_blank" rel="noreferrer" className="font-medium text-slate-800 hover:text-indigo-600 truncate">{d.filename}</a>
                  <Badge variant="secondary" className="capitalize">{(d.category || "").replace("_", " ")}</Badge>
                  <span className="text-slate-400">{Math.round((d.size_bytes || 0) / 1024)} KB</span>
                  {d.extracted_text && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Text extracted</Badge>}
                </div>
                {userIsAdmin && (
                  <button onClick={() => removeDoc(d.doc_id)} className="text-slate-400 hover:text-red-600" data-testid={`doc-remove-${d.doc_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* REX-DESIGN-LIBRARY (Feb 2026) — Social media graphic examples */}
      <SocialDesignLibrary
        userIsAdmin={userIsAdmin}
        docs={docs.filter(d => d.category === "social_media_design")}
        onChange={loadDocs}
        onRemove={removeDoc}
      />

      {/* Manual viewer + editor */}
      <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="manual-editor">
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Brand Manual</h2>
            <p className="text-xs text-slate-500">
              {manual?.generated_at
                ? <>Version {manual.version || 1} · generated {new Date(manual.generated_at).toLocaleString()} {manual.used_fallback ? <span className="text-amber-700">(fallback)</span> : null}</>
                : "Not generated yet."}
              {manual?.last_edited_at && <> · edited {new Date(manual.last_edited_at).toLocaleString()} by {manual.last_edited_by}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {manual?.generated_at && <Button size="sm" variant="outline" onClick={download} data-testid="manual-download-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export .md</Button>}
            {userIsAdmin && (
              <Button size="sm" onClick={generate} disabled={generating} data-testid="manual-generate-btn">
                {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                {manual?.generated_at ? "Regenerate" : "Generate manual"}
              </Button>
            )}
          </div>
        </div>

        {!manual?.generated_at ? (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-10 text-center">
            <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <div className="text-sm font-medium text-slate-700">No brand manual yet</div>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Fill in Identity, Voice systems, Markets, then click "Generate manual" to have the AI assemble a complete brand manual. You can edit any section afterwards.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
            <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
              {MANUAL_SECTIONS.map(({ k, l }) => (
                <button
                  key={k}
                  onClick={() => setActiveSection(k)}
                  className={`whitespace-nowrap text-left text-xs rounded px-3 py-2 border transition ${
                    activeSection === k
                      ? "bg-indigo-50 text-indigo-700 border-indigo-200 font-medium"
                      : "bg-white text-slate-600 border-transparent hover:bg-slate-50"
                  }`}
                  data-testid={`manual-section-${k}`}
                >
                  {l}
                </button>
              ))}
            </nav>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Section</Label>
              <div className="text-sm font-semibold mb-2">{MANUAL_SECTIONS.find(s => s.k === activeSection)?.l}</div>
              <Textarea
                disabled={!userIsAdmin}
                value={draft[activeSection] || ""}
                onChange={e => setDraft(p => ({ ...p, [activeSection]: e.target.value }))}
                rows={18}
                className="font-mono text-[13px] leading-6"
                data-testid={`manual-textarea-${activeSection}`}
                placeholder="Markdown supported. Edit freely — this is your brand manual now."
              />
              {userIsAdmin && dirty && (
                <div className="flex justify-end mt-3">
                  <Button size="sm" onClick={saveManual} disabled={saving} data-testid="manual-save-btn">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save sections
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// SocialDesignLibrary — REX-DESIGN-LIBRARY (Feb 2026)
// ============================================================================
// Bulk-upload past social-media designs as a ZIP. Each file is unpacked,
// stored, and auto-classified via the OpenAI vision LLM (voice / format /
// theme / dominant colours / 1-line description). Rex uses these
// downstream as direct image prompts for AI image generation.
// ============================================================================
const VOICE_LABELS = {
  remi_personal: { label: "Remi", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  resiligent_institutional: { label: "RESILIGENT", cls: "bg-slate-200 text-slate-900 border-slate-400" },
  employee_voice: { label: "Employee", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  mixed: { label: "Mixed", cls: "bg-violet-100 text-violet-900 border-violet-300" },
  unknown: { label: "Unknown", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

const FORMAT_LABELS = [
  "single_post", "carousel_slide", "quote_card", "testimonial",
  "infographic", "event_promo", "team_photo", "headshot",
  "brochure_excerpt", "report_cover", "logo_lockup", "other",
];

function SocialDesignLibrary({ userIsAdmin, docs, onChange, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [voiceFilter, setVoiceFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [similarFor, setSimilarFor] = useState(null);  // seed doc when "Find similar" is clicked

  const uploadZip = async (file) => {
    if (!userIsAdmin) { toast.error("Designer or admin role required"); return; }
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip archive");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("ZIP must be under 200 MB");
      return;
    }
    setUploading(true);
    setProgress({ stage: "uploading", filename: file.name });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("classify", "true");
      const r = await apiCall("/brand-manager/documents/zip", { method: "POST", body: fd });
      const d = await r.json();
      if (r.ok) {
        toast.success(
          `Uploaded ${d.uploaded_count} of ${d.total} designs · ${d.classified} auto-classified` +
          (d.skipped_count ? ` · ${d.skipped_count} skipped` : "")
        );
        onChange();
      } else {
        toast.error(d.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed: " + e.message);
    }
    setUploading(false);
    setProgress(null);
  };

  const updateTags = async (docId, tags) => {
    const r = await apiCall(`/brand-manager/documents/${docId}/tags`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ auto_tags: tags }),
    });
    if (r.ok) {
      toast.success("Tags updated");
      setEditing(null);
      onChange();
    } else {
      toast.error("Update failed");
    }
  };

  const filtered = voiceFilter
    ? docs.filter((d) => d.auto_tags?.voice === voiceFilter)
    : docs;
  const voiceCounts = docs.reduce((acc, d) => {
    const v = d.auto_tags?.voice || "unknown";
    acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-5 space-y-4" data-testid="social-design-library">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-1.5">
            <ImageIcon className="h-4 w-4 text-indigo-700" /> Upload social media graphic examples
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-2xl">
            Drop a <span className="font-mono bg-white px-1 rounded border">.zip</span> of past social-media designs (PNG / JPG / WEBP / GIF / PDF / PSD / AI). Rex unpacks them, runs vision analysis on each image, and auto-tags voice, format, theme + colours. The library becomes a reference set for future AI-generated images and designer briefs.
          </p>
        </div>
        {userIsAdmin && (
          <label className="inline-flex shrink-0">
            <input
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadZip(e.target.files[0])}
              disabled={uploading}
              data-testid="design-zip-input"
            />
            <span className={`inline-flex items-center gap-1.5 text-sm font-medium border rounded-md px-3 py-2 cursor-pointer transition ${uploading ? "bg-slate-100 text-slate-400 cursor-wait" : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? `Processing ${progress?.filename || "ZIP"}…` : "Upload ZIP"}
            </span>
          </label>
        )}
      </div>

      {uploading && (
        <div className="text-xs text-indigo-700 bg-white rounded border border-indigo-200 px-3 py-2">
          Extracting + classifying. This can take ~2–3 seconds per image. Don't navigate away.
        </div>
      )}

      {docs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Filter:</span>
          <button
            onClick={() => setVoiceFilter("")}
            className={`text-[10px] uppercase tracking-wider font-bold rounded px-2 py-0.5 border transition ${voiceFilter === "" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
            data-testid="design-voice-filter-all"
          >All ({docs.length})</button>
          {Object.entries(VOICE_LABELS).map(([k, v]) => {
            if (!voiceCounts[k]) return null;
            return (
              <button
                key={k}
                onClick={() => setVoiceFilter(k)}
                className={`text-[10px] uppercase tracking-wider font-bold rounded px-2 py-0.5 border transition ${voiceFilter === k ? v.cls + " ring-2 ring-indigo-400" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                data-testid={`design-voice-filter-${k}`}
              >{v.label} ({voiceCounts[k]})</button>
            );
          })}
        </div>
      )}

      {docs.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-6 text-center bg-white rounded border border-dashed border-slate-300">
          No design samples yet. Upload a ZIP to seed the library.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="design-grid">
          {filtered.map((d) => {
            const tags = d.auto_tags || {};
            const v = VOICE_LABELS[tags.voice] || VOICE_LABELS.unknown;
            const isImage = (d.content_type || "").startsWith("image/");
            return (
              <div key={d.doc_id} className="rounded-lg overflow-hidden bg-white border border-slate-200 hover:border-indigo-300 transition group relative" data-testid={`design-card-${d.doc_id}`}>
                {isImage ? (
                  <a href={d.url} target="_blank" rel="noreferrer" className="block bg-slate-100 aspect-square overflow-hidden">
                    <img src={d.url} alt={d.filename} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
                  </a>
                ) : (
                  <a href={d.url} target="_blank" rel="noreferrer" className="bg-slate-100 aspect-square flex items-center justify-center text-slate-400">
                    <FileText className="h-12 w-12" />
                  </a>
                )}
                <div className="p-2 space-y-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border ${v.cls}`}>
                      {v.label}
                    </span>
                    {tags.format && tags.format !== "other" && (
                      <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-[1px] border bg-slate-50 text-slate-700 border-slate-300">
                        {tags.format.replace(/_/g, " ")}
                      </span>
                    )}
                    {tags.confidence === "low" && (
                      <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-[1px] border bg-amber-50 text-amber-700 border-amber-200" title="Low-confidence auto-tag — review">!</span>
                    )}
                    {tags.manually_edited && (
                      <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-[1px] border bg-emerald-50 text-emerald-700 border-emerald-200">edited</span>
                    )}
                  </div>
                  {tags.theme && <p className="text-xs font-medium text-slate-800 truncate" title={tags.theme}>{tags.theme}</p>}
                  {tags.description && <p className="text-[11px] text-slate-500 line-clamp-2" title={tags.description}>{tags.description}</p>}
                  {tags.dominant_colours?.length > 0 && (
                    <div className="flex gap-0.5">
                      {tags.dominant_colours.slice(0, 5).map((c, i) => (
                        <span key={i} className="h-3 w-3 rounded-sm border border-slate-200" style={{ backgroundColor: c }} title={c} />
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 truncate" title={d.filename}>{d.filename}</p>
                  {userIsAdmin && (
                    <div className="flex justify-between items-center pt-1 border-t border-slate-100">
                      <button
                        onClick={() => setEditing(d.doc_id)}
                        className="text-[10px] text-indigo-600 hover:underline"
                        data-testid={`design-edit-${d.doc_id}`}
                      >Edit tags</button>
                      <button
                        onClick={() => setSimilarFor(d)}
                        className="text-[10px] text-indigo-600 hover:underline"
                        data-testid={`design-similar-${d.doc_id}`}
                      >Find similar</button>
                      <button
                        onClick={() => onRemove(d.doc_id)}
                        className="text-[10px] text-slate-400 hover:text-red-600"
                        data-testid={`design-remove-${d.doc_id}`}
                      ><Trash2 className="h-3 w-3" /></button>
                    </div>
                  )}
                </div>
                {editing === d.doc_id && (
                  <DesignTagEditor
                    doc={d}
                    onSave={(tags) => updateTags(d.doc_id, tags)}
                    onCancel={() => setEditing(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {similarFor && (
        <FindSimilarModal
          seed={similarFor}
          onClose={() => setSimilarFor(null)}
        />
      )}
    </section>
  );
}

// REX-DESIGN-LIBRARY follow-up — "Find similar" affordance.
function FindSimilarModal({ seed, onClose }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await apiCall(`/brand-manager/documents/${seed.doc_id}/similar?limit=5`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Failed");
        if (!cancel) setResults(d.results || []);
      } catch (e) {
        if (!cancel) setError(e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [seed.doc_id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="find-similar-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 p-4 border-b border-slate-200 bg-slate-50">
          <img src={seed.url} alt="" className="h-16 w-16 rounded object-cover border border-slate-200" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Find similar to</p>
            <p className="text-sm font-semibold truncate">{seed.filename}</p>
            <p className="text-xs text-slate-500 truncate">
              {(seed.auto_tags?.voice || "").replace(/_/g, " ")} · {(seed.auto_tags?.format || "").replace(/_/g, " ")}
              {seed.auto_tags?.theme && <> · {seed.auto_tags.theme}</>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="find-similar-close"><Trash2 className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Searching library…</div>
          ) : error ? (
            <p className="text-sm text-rose-600">Failed: {error}</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-8 text-center">
              No similar designs found. Upload more samples to build the library.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="find-similar-results">
              {results.map((r) => {
                const isImage = (r.content_type || "").startsWith("image/");
                const v = VOICE_LABELS[r.auto_tags?.voice] || VOICE_LABELS.unknown;
                return (
                  <div key={r.doc_id} className="rounded-lg overflow-hidden bg-white border border-slate-200" data-testid={`find-similar-row-${r.doc_id}`}>
                    {isImage ? (
                      <a href={r.url} target="_blank" rel="noreferrer" className="block aspect-square bg-slate-100 overflow-hidden">
                        <img src={r.url} alt={r.filename} className="w-full h-full object-cover" loading="lazy" />
                      </a>
                    ) : (
                      <div className="aspect-square bg-slate-100 flex items-center justify-center text-slate-400">
                        <FileText className="h-12 w-12" />
                      </div>
                    )}
                    <div className="p-2 space-y-0.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`text-[9px] uppercase tracking-wider font-bold rounded px-1.5 py-[1px] border ${v.cls}`}>{v.label}</span>
                        <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-[1px] border bg-slate-50 text-slate-700 border-slate-300">
                          score {r.score}
                        </span>
                      </div>
                      {r.auto_tags?.theme && <p className="text-xs font-medium text-slate-800 truncate">{r.auto_tags.theme}</p>}
                      <p className="text-[10px] text-slate-400 truncate">{r.filename}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end">
          <button onClick={onClose} className="text-sm bg-slate-100 hover:bg-slate-200 rounded-md px-3 py-1.5" data-testid="find-similar-done">Done</button>
        </div>
      </div>
    </div>
  );
}


function DesignTagEditor({ doc, onSave, onCancel }) {
  const t = doc.auto_tags || {};
  const [voice, setVoice] = useState(t.voice || "unknown");
  const [format, setFormat] = useState(t.format || "other");
  const [theme, setTheme] = useState(t.theme || "");
  const [description, setDescription] = useState(t.description || "");
  return (
    <div className="absolute inset-0 bg-white p-3 z-10 flex flex-col text-xs space-y-2 border-2 border-indigo-400 rounded-lg" data-testid={`design-tag-editor-${doc.doc_id}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Edit tags</p>
      <label className="flex flex-col">
        <span className="text-[10px] text-slate-500">Voice</span>
        <select value={voice} onChange={(e) => setVoice(e.target.value)} className="text-xs border border-slate-300 rounded px-1 py-0.5">
          {Object.keys(VOICE_LABELS).map((k) => <option key={k} value={k}>{VOICE_LABELS[k].label}</option>)}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-slate-500">Format</span>
        <select value={format} onChange={(e) => setFormat(e.target.value)} className="text-xs border border-slate-300 rounded px-1 py-0.5">
          {FORMAT_LABELS.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-slate-500">Theme</span>
        <input value={theme} onChange={(e) => setTheme(e.target.value)} className="text-xs border border-slate-300 rounded px-1 py-0.5" />
      </label>
      <label className="flex flex-col flex-1">
        <span className="text-[10px] text-slate-500">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="text-xs border border-slate-300 rounded px-1 py-0.5 flex-1" rows={3} />
      </label>
      <div className="flex justify-end gap-1 pt-1">
        <button onClick={onCancel} className="text-[10px] text-slate-500 hover:text-slate-700 px-2 py-1" data-testid={`design-tag-cancel-${doc.doc_id}`}>Cancel</button>
        <button
          onClick={() => onSave({ voice, format, theme, description })}
          className="text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded"
          data-testid={`design-tag-save-${doc.doc_id}`}
        >Save</button>
      </div>
    </div>
  );
}


